import { useEffect, useId, useRef, useState, type CSSProperties } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Glass } from '@/components/ui/glass'
import { cn } from '@/lib/utils'

type Option = { value: string; label: string }

/**
 * Single-choice filter dropdown on Liquid Glass, matching FacetFilter. Replaces native <select> in filter bars,
 * whose open list the OS draws and CSS cannot style. Field passes id and aria-* through to the trigger.
 */
export function GlassSelect({
  value,
  onChange,
  options,
  id,
  className,
  triggerClassName,
  disabled,
  up = false,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: {
  value: string
  onChange: (next: string) => void
  options: Option[]
  id?: string
  className?: string
  triggerClassName?: string
  disabled?: boolean
  /** Open above the trigger, for controls pinned to the bottom of the screen. */
  up?: boolean
  'aria-label'?: string
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}) {
  const [open, setOpen] = useState(false)
  // Viewport coordinates for the list. Fixed, not absolute: an absolute list inside a dialog or any
  // overflow container is clipped by it and stretches its scroll height.
  const [box, setBox] = useState<CSSProperties>({})
  const root = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([])
  const listId = useId()
  const current = options.find((o) => o.value === value)

  const place = () => {
    const r = trigger.current?.getBoundingClientRect()
    if (!r) return
    setBox(up ? { bottom: window.innerHeight - r.top + 6, left: r.left, minWidth: r.width } : { top: r.bottom + 6, left: r.left, minWidth: r.width })
  }
  const show = () => {
    place()
    setOpen(true)
  }

  useEffect(() => {
    if (!open) return
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  })

  useEffect(() => {
    if (!open) return
    optionRefs.current[Math.max(0, options.findIndex((o) => o.value === value))]?.focus()
    const onDoc = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open, options, value])

  const close = () => {
    setOpen(false)
    trigger.current?.focus()
  }
  const move = (step: number) => {
    const at = optionRefs.current.findIndex((el) => el === document.activeElement)
    optionRefs.current[(at + step + options.length) % options.length]?.focus()
  }

  return (
    <div
      ref={root}
      className={cn('relative', className)}
      onKeyDown={(e) => {
        if (!open) return
        if (e.key === 'Escape') {
          e.preventDefault()
          e.stopPropagation()
          close()
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault()
          move(e.key === 'ArrowDown' ? 1 : -1)
        } else if (e.key === 'Tab') {
          setOpen(false)
        }
      }}
    >
      <Button
        ref={trigger}
        id={id}
        type="button"
        variant="outline"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
        disabled={disabled}
        className={cn('h-9 w-full justify-between font-normal', triggerClassName)}
        onClick={() => (open ? setOpen(false) : show())}
        onKeyDown={(e) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            e.preventDefault()
            show()
          }
        }}
      >
        <span className="truncate">{current?.label ?? ''}</span>
        <ChevronsUpDown className="size-4 shrink-0 opacity-60" aria-hidden />
      </Button>
      {open ? (
        <Glass radius={14} id={listId} role="listbox" className="fixed z-40 p-1.5" style={box}>
          <div>
            {options.map((o, i) => {
              const on = o.value === value
              return (
                <button
                  key={o.value || 'all'}
                  ref={(el) => {
                    optionRefs.current[i] = el
                  }}
                  type="button"
                  role="option"
                  aria-selected={on}
                  className="flex min-h-8 w-full items-center gap-2 rounded-lg px-2.5 text-left text-sm whitespace-nowrap hover:bg-[var(--glass-hover)] focus-visible:bg-[var(--glass-hover)] focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-ring max-sm:min-h-11"
                  onClick={() => {
                    onChange(o.value)
                    close()
                  }}
                >
                  <Check className={cn('size-4 shrink-0', on ? 'opacity-100' : 'opacity-0')} aria-hidden />
                  {o.label}
                </button>
              )
            })}
          </div>
        </Glass>
      ) : null}
    </div>
  )
}
